import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    Typography,
    IconButton,
    Grid,
    Card,
    Dialog,
    DialogTitle,
    DialogContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from "@mui/material";
import {
    AttachFile as AttachFileIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Download as DownloadIcon,
    Close as CloseIcon, // Cancel icon
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import vendorIntService from "@/modules/vendor/service/vendor.int.service";
import ToastService from "@/service/toaster.service";
import vendorAuthService from "@/service/auth.service/vendor.auth.service";
import vendorService from "@/service/vender.service";
import { setAuthInfo } from "@/redux/auth/auth.slice";
import TwoFactorCodeModal from "@/components/ChatComponent/TwoFactorCodeModal";
import SettingsRedirectModal from "@/components/ChatComponent/SettingsRedirectModal";


const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const MAX_FILE_COUNT = 5;

const FileUploader = ({ fileType, fileInputRef, handleFileChange, files, pendingUploads, setFiles, setPendingUploads, renderFilePreview, handlePendingFileRemove }) => {
    const handleUploadChange = (e) => handleFileChange(e, fileType);

    const handleFileRemove = (index) => handlePendingFileRemove(fileType, index);

    return (
        <>
            {pendingUploads[fileType]?.length > 0 && (
                <Box mt={1} display="flex" flexWrap="wrap" gap={2}>
                    {pendingUploads[fileType].map((file, index) => (
                        <Box key={index} position="relative" sx={{ maxWidth: 120, maxHeight: 120 }}>
                            {renderFilePreview(file)}
                            <IconButton
                                size="small"
                                onClick={() => handleFileRemove(index)}
                                sx={{
                                    position: "absolute",
                                    top: -10,
                                    right: -10,
                                    color: "red",
                                    background: "#fff",
                                }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    ))}
                </Box>
            )}
            <input
                type="file"
                accept="application/pdf, image/*"
                multiple
                ref={fileInputRef}
                onChange={handleUploadChange}
            />
        </>
    );
};

const AttachmentSection = () => {
    const user = useSelector((state) => state.auth.auth);
    const dispatch = useDispatch();
    const vendorId = user.id;
    const [isEditing, setIsEditing] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentDialogFiles, setCurrentDialogFiles] = useState([]);
    const [currentFiles, setCurrentFiles] = useState();
    const [currentDialogType, setCurrentDialogType] = useState("");
    const licenseInputRef = useRef(null);
    const certificateInputRef = useRef(null);
    const [pendingUploads, setPendingUploads] = useState({
        licenses: [],
        certificates: [],
    });
    const [files, setFiles] = useState({
        licenses: [],
        certificates: [],
    });
    const [twoFactorModalOpen, setTwoFactorModalOpen] = useState(false);
    const [settingsModalOpen, setSettingsModalOpen] = useState(false);
    const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

    useEffect(() => {
        const fetchAttachments = async () => {
            try {
                const attachments = await vendorService.getVendorAttachments(vendorId);
                setFiles({
                    licenses: (attachments.licenses || []).map((file) => ({
                        ...file,
                        file_type: file.file_type || "image/png",
                        file_url: file.file_url,
                    })),
                    certificates: (attachments.certificates || []).map((file) => ({
                        ...file,
                        file_type: file.file_type || "image/png",
                        file_url: file.file_url,
                    })),
                });

                // Store two-factor authentication status
                setTwoFactorEnabled(attachments.two_factor_enabled);
            } catch (error) {
                console.error("Error fetching attachments:", error);
                // ToastService.error("Failed to load attachments");
            }
        };

        if (user) {
            fetchAttachments();
        }
    }, [user]);

    const handleFileChange = (e, fileType) => {
        const newFiles = Array.from(e.target.files);
        const validFiles = newFiles.filter((file) => {
            // Check if file size is within limit
            return file.size <= MAX_FILE_SIZE;
        });

        if (validFiles.length !== newFiles.length) {
            ToastService.error("One or more files exceed the 10 MB limit.");
        }

        // Get the current count of files for the specific fileType
        const currentFilesCount = (files[fileType]?.length || 0) + (pendingUploads[fileType]?.length || 0);

        // Ensure that no more than 5 files are selected
        if (validFiles.length > MAX_FILE_COUNT) {
            ToastService.error(`You can upload a maximum of ${MAX_FILE_COUNT}file(s).`);
            return;
        }

        const uploadedFiles = validFiles.map((file) => ({
            original_name: file.name,
            file_url: URL.createObjectURL(file),
            file_type: file.type,
            uploaded_at: new Date().toISOString(),
            local_file: file,
        }));

        // Store selected files in pendingUploads
        setPendingUploads((prev) => ({
            ...prev,
            [fileType]: [...(prev[fileType] || []), ...uploadedFiles],
        }));
    };

    const toggleEditMode = async () => {
        if (isEditing) {
            try {
                const formData = new FormData();

                // Append all license files
                pendingUploads.licenses.forEach((fileObj) => {
                    formData.append("licenses", fileObj.local_file);
                });

                // Append all certificate files
                pendingUploads.certificates.forEach((fileObj) => {
                    formData.append("certificates", fileObj.local_file);
                });

                if (formData.has("licenses") || formData.has("certificates")) {
                    const result = await vendorService.updateVendorProfile(vendorId, formData);

                    // Refresh attachments from the new API
                    const attachments = await vendorService.getVendorAttachments(vendorId);
                    setFiles({
                        licenses: (attachments.licenses || []).map((file) => ({
                            ...file,
                            file_type: file.file_type || "image/png",
                            file_url: file.file_url,
                        })),
                        certificates: (attachments.certificates || []).map((file) => ({
                            ...file,
                            file_type: file.file_type || "image/png",
                            file_url: file.file_url,
                        })),
                    });

                    ToastService.success("Files uploaded successfully.");
                    setPendingUploads({ licenses: [], certificates: [] });
                }
            } catch (error) {
                console.error("File upload failed", error);
                ToastService.error("File upload failed.");
            }
        }

        setIsEditing((prev) => !prev);
    };

    const openDialog = (fileType) => {
        // Store the file type for later use
        setCurrentDialogFiles(files[fileType]);
        setCurrentDialogType(fileType);
        setDialogOpen(true);
    };

    const openPreview = (fileType, index) => {
        // Store the file type for later use
        setCurrentDialogType(fileType);
        // Check two-factor authentication status before opening preview
        if (twoFactorEnabled) {
            setCurrentFiles(files[fileType][index]);
            setTwoFactorModalOpen(true);
        } else {
            setSettingsModalOpen(true);
        }
    };

    const renderFilePreview = (file) => {
        if (file.file_type.startsWith("image/")) {
            return (
                <AttachFileIcon sx={{ color: "#1E40AF", fontSize: 40 }} />
            );
        } else if (file.file_type === "application/pdf") {
            return (
                <AttachFileIcon sx={{ color: "#1E40AF", fontSize: 40 }} />
            );
        } else {
            return (
                <AttachFileIcon sx={{ color: "#1E40AF", fontSize: 40 }} />
            );
        }
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setCurrentDialogFiles([]);
        setCurrentDialogType("");
    };


    const handlePendingFileRemove = (fileType, index) => {
        const updatedPending = pendingUploads[fileType].filter((_, i) => i !== index);

        setPendingUploads((prev) => ({
            ...prev,
            [fileType]: updatedPending,
        }));

        setFiles((prev) => ({
            ...prev,
            [fileType]: updatedPending,
        }));

        // Reset input file if all pending files are removed
        if (fileType === "licenses" && updatedPending.length === 0 && licenseInputRef.current) {
            licenseInputRef.current.value = "";
        }
        if (fileType === "certificates" && updatedPending.length === 0 && certificateInputRef.current) {
            certificateInputRef.current.value = "";
        }
    };

    const handleFileDelete = async (fileType, fileToDelete) => {
        // Filter out the file to delete from both files and dialog-specific files
        const updatedFiles = files[fileType].filter(
            (file) => file.original_name !== fileToDelete.original_name
        );

        const updatedDialogFiles = currentDialogFiles.filter(
            (file) => file.original_name !== fileToDelete.original_name
        );

        const formData = new FormData();
        formData.append(fileType, JSON.stringify(updatedFiles)); // Send the new updated list

        try {

            console.log("Deleting file", updatedDialogFiles);
            await vendorService.updateVendorProfile(vendorId, formData);

            // Refresh attachments from the new API
            const attachments = await vendorService.getVendorAttachments(vendorId);
            setFiles({
                licenses: (attachments.licenses || []).map((file) => ({
                    ...file,
                    file_type: file.file_type || "image/png",
                    file_url: file.file_url,
                })),
                certificates: (attachments.certificates || []).map((file) => ({
                    ...file,
                    file_type: file.file_type || "image/png",
                    file_url: file.file_url,
                })),
            });

            // Also update the dialog files to reflect the deletion
            setCurrentDialogFiles(updatedFiles);

            ToastService.success("File deleted successfully.");
        } catch (error) {
            console.error("Failed to delete file", error);
            ToastService.error("Failed to delete file");
        }
    };

    return (
        <Card sx={{ p: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Box>
                    <Typography variant="h6">Attachments (Licenses and Certificates)</Typography>
                    {/* Info Label */}
                    <Typography
                        variant="body2"
                        sx={{ fontStyle: 'italic', mt: 0.5, color: '#666' }}
                    >
                        Max 5 files can be uploaded and total size should not exceed 10 MB.
                    </Typography>
                </Box>
                <Button
                    startIcon={<EditIcon sx={{ color: isEditing ? "#1E40AF" : "grey" }} />}
                    size="small"
                    sx={{
                        borderRadius: 2,
                        border: "1px solid #D3D3D3",
                        backgroundColor: "transparent",
                        color: isEditing ? "#1E40AF" : "grey",
                        "&:hover": { borderColor: "#1E40AF", color: "#1E40AF" },
                    }}
                    onClick={toggleEditMode}
                >
                    {isEditing ? "Save" : "Edit"}
                </Button>
            </Box>

            <Box mb={2}>
                <Typography variant="body1" sx={{ mb: 1 }}>Licenses</Typography>
                {isEditing && (
                    <FileUploader
                        fileType="licenses"
                        fileInputRef={licenseInputRef}
                        handleFileChange={handleFileChange}
                        files={files}
                        pendingUploads={pendingUploads}
                        setFiles={setFiles}
                        setPendingUploads={setPendingUploads}
                        renderFilePreview={renderFilePreview}
                        handlePendingFileRemove={handlePendingFileRemove}
                    />
                )}
                <Button variant="outlined" size="small" sx={{ mt: 1 }} onClick={() => openDialog("licenses")}>View Licenses</Button>
            </Box>

            <Box mb={2}>
                <Typography variant="body1" sx={{ mb: 1 }}>Certificates</Typography>
                {isEditing && (
                    <FileUploader
                        fileType="certificates"
                        fileInputRef={certificateInputRef}
                        handleFileChange={handleFileChange}
                        files={files}
                        pendingUploads={pendingUploads}
                        setFiles={setFiles}
                        setPendingUploads={setPendingUploads}
                        renderFilePreview={renderFilePreview}
                        handlePendingFileRemove={handlePendingFileRemove}
                    />
                )}
                <Button variant="outlined" size="small" sx={{ mt: 1 }} onClick={() => openDialog("certificates")}>View Certificates</Button>
            </Box>

            <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="h6">{currentDialogType === "licenses" ? "Licenses" : "Certificates"}</Typography>
                    <IconButton onClick={closeDialog}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>File Name</TableCell>
                                    <TableCell>File Type</TableCell>
                                    <TableCell>Preview</TableCell> {/* Add this new column */}
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {currentDialogFiles.map((file, index) => (
                                    <TableRow key={file.original_name}>
                                        <TableCell>{file.original_name}</TableCell>
                                        <TableCell>{file.file_type}</TableCell>
                                        <TableCell
                                            onClick={() => openPreview(currentDialogType, index)}
                                            sx={{ mr: 1, cursor: "pointer" }}
                                            title="Preview File">
                                            {renderFilePreview(file)} {/* Display the file preview */}
                                        </TableCell>
                                        <TableCell>
                                            <IconButton onClick={() => handleFileDelete(currentDialogType, file)}>
                                                <DeleteIcon sx={{ color: "red" }} />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>

            </Dialog>

            {/* Two Factor Code Modal */}
            <TwoFactorCodeModal
                open={twoFactorModalOpen}
                onClose={() => setTwoFactorModalOpen(false)}
                onSuccess={(res) => {
                    setTwoFactorModalOpen(false);
                    // Handle successful 2FA verification and open file in new tab
                    ToastService.success("Two-factor authentication verified successfully");
                    // Open the file in a new tab
                    if (res) {
                        window.open(res, '_blank');
                    }
                }}
                fileName={currentFiles?.original_name}
                s3Path={currentFiles?.file_url}
                role="vendor"
            />

            {/* Settings Redirect Modal */}
            <SettingsRedirectModal
                open={settingsModalOpen}
                onClose={() => setSettingsModalOpen(false)}
                userRole="vendor"
            />
        </Card>
    );
};

export default AttachmentSection;
